/**
 * Chat Screen
 * Main conversation interface for practicing English
 */

import React, { useRef, useCallback, useState, useEffect } from "react";
import { FlatList, Keyboard, Platform, View, StyleSheet, TouchableOpacity, Text } from "react-native";
import { KeyboardAvoidingView } from "react-native-keyboard-controller";

import { WebContainer } from "../components/ui/WebContainer";
import { useTranslation } from "react-i18next";

import { BlurView } from "expo-blur";
import { useFocusEffect, useNavigation, useRoute } from "@react-navigation/native";
import { useThemeColors } from "../theme";
import { useConversationStore } from "../store/conversationStore";
import {
  selectIsSendingMessage,
  selectIsRecording,
  selectMessages,
  selectConversationError,
  selectErrorInfo,
  selectToggleAnalysisExpanded,
  selectClearError,
  selectSendTextMessage,
  selectStartRecording,
  selectStopRecording,
  selectCancelRecording,
  selectRetryConversation,
  selectRetryAnalysis,
  selectLoadSession,
  selectClearMessages,
  selectIsStreaming,
} from "../store/conversationSelectors";
import { useAIConsent } from "../hooks/useAIConsent";
import { AnalyzedMessage, PronunciationResult, LessonReference } from "../types/conversation";
import type { PracticeErrorParams } from "../components/chat/FeedbackCard";

import { MessageBubble } from "../components/chat/MessageBubble";
import { MessageInput } from "../components/chat/MessageInput";
import { VoiceRecorder } from "../components/chat/VoiceRecorder";
import PronunciationPrompt from "../components/chat/PronunciationPrompt";
import { AIConsentModal } from "../components/AIConsentModal";
import { QuickPracticeSheet } from "../components/chat/QuickPracticeSheet";
import { ConnectionStatusBanner } from "../components/chat/ConnectionStatusBanner";
import { useSocketStatus, ensureConnected } from "../services/socket";
import { useQuickPractice } from "../hooks/useQuickPractice";
import { useAudioMode, loadAudioModeCache } from "../hooks/useAudioMode";
import {
  requestMicrophonePermission,
  showPermissionDeniedAlert,
  showPermissionBlockedAlert,
  showMicrophoneUnavailableAlert,
} from "../utils/permissions";
import { api } from "../services/api";
import { createLogger } from "../utils/logger";
import { useTTSStore } from "../store/ttsStore";
import { useTTSSettingsStore } from "../store/ttsSettingsStore";
import { useStreamingTTSStore } from "../store/streamingTTSStore";
import { useAppBannerStore } from "../hooks/useAppBanner";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { ScreenHeader } from "../components/ui/ScreenHeader";

const log = createLogger("ChatScreen");

const KAV = Platform.OS === 'web' ? View : KeyboardAvoidingView;

export function ChatScreen() {
  const flatListRef = useRef<FlatList>(null);
  const theme = useThemeColors();
  const { t } = useTranslation();
  const navigation = useNavigation('Chat');
  const route = useRoute('Chat');
  const routeSessionId = (route.params as { sessionId?: string } | undefined)?.sessionId;

  // AI Consent state
  const { hasConsent, grantConsent, needsReConsent } = useAIConsent();
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<{
    type: "text" | "voice" | "topic";
    payload?: string;
  } | null>(null);

  // ── Load TTS settings + audio mode from AsyncStorage ────────────
  const ttsLoaded = useTTSSettingsStore((s) => s.loaded);
  useEffect(() => {
    if (!ttsLoaded) useTTSSettingsStore.getState().load();
    loadAudioModeCache();
  }, [ttsLoaded]);

  // ── Quick Practice state ─────────────────────────────────────────
  const quickPractice = useQuickPractice();
  const { generate: qpGenerate, clear: qpClear } = quickPractice;
  const [practiceVisible, setPracticeVisible] = useState(false);
  const [practiceParams, setPracticeParams] = useState<PracticeErrorParams | null>(null);

  // ── Store selectors (data + orchestration) ─────────────────────
  const isSending = useConversationStore(selectIsSendingMessage);
  const isRecording = useConversationStore(selectIsRecording);
  const messages = useConversationStore(selectMessages);
  const error = useConversationStore(selectConversationError);
  const errorInfo = useConversationStore(selectErrorInfo);
  const toggleAnalysisExpanded = useConversationStore(selectToggleAnalysisExpanded);
  const clearError = useConversationStore(selectClearError);
  const sendTextMessage = useConversationStore(selectSendTextMessage);
  const storeStartRecording = useConversationStore(selectStartRecording);
  const storeStopRecording = useConversationStore(selectStopRecording);
  const storeCancelRecording = useConversationStore(selectCancelRecording);
  const storeRetryConversation = useConversationStore(selectRetryConversation);
  const storeRetryAnalysis = useConversationStore(selectRetryAnalysis);
  const loadSession = useConversationStore(selectLoadSession);
  const clearMessages = useConversationStore(selectClearMessages);

  // ── Load session from route param or reset for new conversation ──
  useEffect(() => {
    if (routeSessionId) {
      loadSession(routeSessionId);
    } else {
      clearMessages();
    }
  }, [routeSessionId, loadSession, clearMessages]);

  // If server rejects with AI_CONSENT_REQUIRED, show consent modal
  useEffect(() => {
    if (errorInfo?.code === 'AI_CONSENT_REQUIRED') {
      clearError();
      setShowConsentModal(true);
    }
  }, [errorInfo?.code, clearError]);

  /**
   * Check consent before AI actions
   * Returns true if action can proceed, false if consent modal is shown
   */
  const checkConsent = useCallback(
    (actionType: "text" | "voice" | "topic", payload?: string): boolean => {
      if (hasConsent && !needsReConsent) {
        return true;
      }
      // Store the pending action and show modal
      setPendingAction({ type: actionType, payload });
      setShowConsentModal(true);
      return false;
    },
    [hasConsent, needsReConsent]
  );

  // Execute sending text message (after consent check)
  const executeSendText = useCallback(
    async (text: string) => {
      try {
        await sendTextMessage(text);
        // Scroll to bottom after sending
        setTimeout(() => {
          flatListRef.current?.scrollToEnd({ animated: true });
        }, 100);
      } catch (_err) {
        // Error is handled in store
      }
    },
    [sendTextMessage]
  );

  // Handle sending text message (with consent check)
  const handleSendText = useCallback(
    async (text: string) => {
      if (!checkConsent("text", text)) return;
      await executeSendText(text);
    },
    [checkConsent, executeSendText]
  );

  // Execute starting voice recording (after consent check)
  // Self-reference for permission retry is safe — callback is only invoked asynchronously
  /* eslint-disable react-hooks/immutability */
  const executeStartVoice = useCallback(async () => {
    // Request microphone permission first
    const status = await requestMicrophonePermission();

    switch (status) {
      case "granted":
        storeStartRecording();
        break;
      case "denied":
        showPermissionDeniedAlert(() => executeStartVoice());
        break;
      case "blocked":
        showPermissionBlockedAlert();
        break;
      case "unavailable":
        showMicrophoneUnavailableAlert();
        break;
      default:
        // Unknown status - try anyway, native module will handle error
        storeStartRecording();
        break;
    }
  }, [storeStartRecording]);
  /* eslint-enable react-hooks/immutability */

  /**
   * Handle consent granted - execute pending action
   */
  const handleConsentAccept = async () => {
    const granted = await grantConsent();
    // Always dismiss the modal so the failure banner (from useAIConsent)
    // isn't covered by the native Modal overlay. Drop the pending
    // action if grant failed — re-firing it would 403 with
    // AI_CONSENT_REQUIRED and bounce through a modal-reopen cycle.
    setShowConsentModal(false);
    const action = pendingAction;
    setPendingAction(null);

    if (!granted) {
      // Banner already visible. User must re-trigger the action to
      // retry.
      return;
    }

    if (action) {
      const { type, payload } = action;
      // Use setTimeout to ensure state is updated
      setTimeout(() => {
        switch (type) {
          case "text":
            if (payload) executeSendText(payload);
            break;
          case "voice":
            executeStartVoice();
            break;
          case "topic":
            if (payload) executeSendText(payload);
            break;
        }
      }, 100);
    }
  };

  /**
   * Handle consent declined
   */
  const handleConsentDecline = () => {
    setShowConsentModal(false);
    setPendingAction(null);
  };

  // Handle starting voice recording with permission check (with consent check)
  const handleStartVoice = useCallback(async () => {
    if (!checkConsent("voice")) return;
    Keyboard.dismiss();
    await executeStartVoice();
  }, [checkConsent, executeStartVoice]);

  // Handle stopping voice recording
  const handleStopVoice = useCallback(
    async (transcription: string, pronunciationResult: PronunciationResult | undefined) => {
      try {
        await storeStopRecording(transcription, pronunciationResult);
        // Scroll to bottom after sending
        setTimeout(() => {
          flatListRef.current?.scrollToEnd({ animated: true });
        }, 100);
      } catch (_err) {
        // Error is handled in store
      }
    },
    [storeStopRecording]
  );

  // Handle canceling voice recording
  const handleCancelVoice = useCallback(() => {
    storeCancelRecording();
  }, [storeCancelRecording]);



  // Handle toggle analysis expansion
  const handleToggleExpand = useCallback(
    (messageId: string) => {
      toggleAnalysisExpanded(messageId);
    },
    [toggleAnalysisExpanded]
  );

  // Handle dismiss for session errors (user navigates back to hub to retry)
  const handleDismissError = useCallback(() => {
    clearError();
  }, [clearError]);

  // Handle retry for failed message analysis (analysis-only)
  const handleRetryAnalysis = useCallback(
    (messageId: string) => {
      storeRetryAnalysis(messageId);
    },
    [storeRetryAnalysis]
  );

  // Handle retry for failed conversation (AI response failed, or user delivery failed)
  const handleRetryConversation = useCallback(
    (messageId: string) => {
      storeRetryConversation(messageId);
    },
    [storeRetryConversation]
  );

  // Handle report AI content (required by Google Play for AI-generated content)
  const showBanner = useAppBannerStore((s) => s.showBanner);
  const [reportDialog, setReportDialog] = useState<{ messageId: string } | null>(null);

  const handleReportAI = useCallback(
    (messageId: string) => {
      setReportDialog({ messageId });
    },
    []
  );

  const submitReport = useCallback(async () => {
    if (!reportDialog) return;
    const { messageId } = reportDialog;
    setReportDialog(null);

    const currentMessages = useConversationStore.getState().messages;
    const message = currentMessages.find((m) => m.id === messageId);
    const messageText =
      message?.type === "text" ? message?.text : message?.transcription;

    const result = await api.reportAIContent({
      messageId,
      messageText: messageText?.substring(0, 2000),
      aiProvider: "google-gemini",
      analysisData: message?.analysis,
    });

    if (result.ok) {
      log.info("AI content report submitted", {
        reportId: result.data.reportId,
        duplicate: result.data.duplicate,
      });

      const confirmMessage = result.data.duplicate
        ? t('chat.report.duplicateMessage')
        : t('chat.report.successMessage');

      showBanner({ message: confirmMessage, type: 'success' });
    } else {
      log.error("Failed to submit AI report", { kind: result.kind, error: result.error });
      showBanner({ message: t('chat.report.successMessage'), type: 'success' });
    }
  }, [reportDialog, t, showBanner]);

  // Navigate to lesson detail when tapping a lesson link in the FeedbackCard
  const handleLessonPress = useCallback(
    (lesson: LessonReference) => {
      navigation.navigate('LessonDetail', { slug: lesson.slug });
    },
    [navigation]
  );

  // Handle practice press from FeedbackCard grammar error
  const handlePracticePress = useCallback(
    (params: PracticeErrorParams) => {
      setPracticeParams(params);
      setPracticeVisible(true);
      qpGenerate(params);
    },
    [qpGenerate]
  );

  const handlePracticeClose = useCallback(() => {
    setPracticeVisible(false);
    qpClear();
  }, [qpClear]);

  const handlePracticeRetry = useCallback(() => {
    if (practiceParams) {
      qpGenerate(practiceParams);
    }
  }, [practiceParams, qpGenerate]);

  // Handle selecting a quick-option from a QuestionCard directive
  const handleSelectOption = useCallback(
    (optionText: string) => handleSendText(optionText),
    [handleSendText]
  );

  // Render message item — pass handlers directly for stable references (React.memo friendly)
  const renderMessage = useCallback(
    ({ item }: { item: AnalyzedMessage }) => (
      <MessageBubble
        message={item}
        onToggleExpand={handleToggleExpand}
        onRetry={handleRetryConversation}
        onRetryConversation={handleRetryConversation}
        onRetryAnalysis={handleRetryAnalysis}
        onReportAI={handleReportAI}
        onLessonPress={handleLessonPress}
        onPracticePress={handlePracticePress}
        onSelectOption={handleSelectOption}
      />
    ),
    [handleToggleExpand, handleRetryConversation, handleRetryAnalysis, handleReportAI, handleLessonPress, handlePracticePress, handleSelectOption]
  );

  // Key extractor
  const keyExtractor = useCallback((item: AnalyzedMessage) => item.id, []);

  // Auto-scroll only when new messages are added (not on every layout change)
  const prevMessageCount = useRef(messages.length);
  useEffect(() => {
    if (messages.length > prevMessageCount.current) {
      setTimeout(() => {
        flatListRef.current?.scrollToEnd({ animated: true });
      }, 100);
    }
    prevMessageCount.current = messages.length;
  }, [messages.length]);

  // ── Auto-play TTS when AI response completes ─────────────────────
  // Only fires when audioMode is 'auto_play'. Skips if streaming TTS already handled it.
  const isStoreStreaming = useConversationStore(selectIsStreaming);
  const wasStreaming = useRef(false);
  const { mode: audioMode } = useAudioMode();
  useEffect(() => {
    if (wasStreaming.current && !isStoreStreaming) {
      const streamingTTSStatus = useStreamingTTSStore.getState().status;
      const streamingTTSHandled = streamingTTSStatus === 'active' || streamingTTSStatus === 'finishing' || streamingTTSStatus === 'done' || streamingTTSStatus === 'canceled' || streamingTTSStatus === 'error';
      if (audioMode === 'auto_play' && !streamingTTSHandled && messages.length > 0) {
        const lastMsg = messages[messages.length - 1];
        const msgText = lastMsg.type === 'voice' ? lastMsg.transcription : lastMsg.text;
        if (lastMsg.role !== 'user' && lastMsg.status === 'complete' && msgText) {
          useTTSStore.getState().speak(
            lastMsg.id,
            msgText,
            useConversationStore.getState().currentSessionId ?? undefined,
          );
        }
      }
    }
    wasStreaming.current = isStoreStreaming;
  }, [isStoreStreaming, messages, audioMode]);

  // Reconnect socket when screen gains focus (e.g. after navigating away for a while)
  useFocusEffect(
    useCallback(() => {
      ensureConnected();
    }, [])
  );

  // Cleanup on unmount: stop TTS and leave socket room
  useEffect(() => {
    return () => {
      useTTSStore.getState().cleanup();
      useStreamingTTSStore.getState().cleanup();
      useConversationStore.getState().leaveCurrentSession();
    };
  }, []);

  // Disable input entirely while sending/streaming.
  // Block send + mic (but allow typing) while socket is not connected.
  const socketStatus = useSocketStatus((s) => s.status);
  const isStreaming = useConversationStore(selectIsStreaming);
  const isInputDisabled = isSending || isStreaming;
  const actionsBlocked = socketStatus !== 'connected';

  // ── Pronunciation assessment directive ─────────────────────────────
  // Active when the last assistant message has a pronunciation_assessment
  // directive and the user hasn't dismissed it manually.
  const [dismissedPromptMessageId, setDismissedPromptMessageId] = useState<string | null>(null);

  const activePronunciationDirective = React.useMemo(() => {
    if (messages.length === 0) return null;
    const lastMsg = messages[messages.length - 1];
    if (
      lastMsg.role === 'user' ||
      lastMsg.type !== 'text' ||
      !lastMsg.directives?.pronunciation_assessment ||
      lastMsg.id === dismissedPromptMessageId ||
      isStreaming
    ) {
      return null;
    }
    return {
      messageId: lastMsg.id,
      directive: lastMsg.directives.pronunciation_assessment,
    };
  }, [messages, dismissedPromptMessageId, isStreaming]);

  const handleDismissPrompt = useCallback(() => {
    if (activePronunciationDirective) {
      setDismissedPromptMessageId(activePronunciationDirective.messageId);
    }
  }, [activePronunciationDirective]);

  return (
    <View style={{ flex: 1, backgroundColor: theme.background }}>
    <ScreenHeader title="Chat" />
    <KAV
      testID="chat-screen"
      style={{ flex: 1 }}
      {...(Platform.OS !== 'web' ? { behavior: 'padding' as const, keyboardVerticalOffset: 0 } : {})}
    >

        {/* Connection error banner — shown only for session creation failures (empty chat) */}
        {error && messages.length === 0 && (
          <TouchableOpacity
            testID="chat-error-banner"
            accessibilityLabel={`${error}. ${errorInfo?.retryable ? t('chat.messages.retryText') : ""}`}
            accessibilityRole="button"
            onPress={handleDismissError}
            activeOpacity={0.7}
            style={{
              backgroundColor: theme.errorBg,
              borderBottomWidth: 1,
              borderBottomColor: theme.error,
              paddingHorizontal: 16,
              paddingVertical: 12,
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <View style={{ flex: 1 }}>
              <Text
                allowFontScaling={false}
                style={{
                  color: theme.error,
                  fontWeight: "600",
                  fontSize: 14,
                }}
              >
                {error}
              </Text>
              {errorInfo?.retryable && (
                <Text
                  allowFontScaling={false}
                  style={{
                    color: theme.error,
                    fontSize: 12,
                    marginTop: 2,
                  }}
                >
                  {t('chat.messages.retryText')}
                </Text>
              )}
            </View>
            <Text
              allowFontScaling={false}
              accessible={false}
              importantForAccessibility="no"
              style={{ fontSize: 20 }}
            >
              {errorInfo?.retryable ? "🔄" : "✕"}
            </Text>
          </TouchableOpacity>
        )}

        {/* Content area */}
        <FlatList
          ref={flatListRef}
          style={{ flex: 1 }}
          data={messages}
          renderItem={renderMessage}
          keyExtractor={keyExtractor}
          contentContainerStyle={{
            flexGrow: 1,
            paddingTop: 16,
            paddingBottom: 16,
            maxWidth: 672,
            alignSelf: 'center' as const,
            width: '100%',
          }}
          showsVerticalScrollIndicator={true}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode={Platform.OS === 'ios' ? 'interactive' : 'on-drag'}
          windowSize={7}
          maxToRenderPerBatch={5}
          initialNumToRender={10}
          removeClippedSubviews={Platform.OS !== "web"}
        />

        {/* Blur overlay when recording (no FlatList duplication) */}
        {isRecording && (
          <BlurView
            intensity={20}
            tint={theme.isDark ? "dark" : "light"}
            style={styles.blurOverlay}
            pointerEvents="none"
          />
        )}

        {/* Connection status banner */}
        <ConnectionStatusBanner />

        {/* Pronunciation assessment prompt (above input when directive is active) */}
        {activePronunciationDirective && (
          <WebContainer variant="chat">
            <PronunciationPrompt
              directive={activePronunciationDirective.directive}
              onDismiss={handleDismissPrompt}
            />
          </WebContainer>
        )}

        {/* Message input */}
        <WebContainer variant="chat">
          <MessageInput
            onSendText={handleSendText}
            onStartVoice={handleStartVoice}
            isDisabled={isInputDisabled}
            actionsBlocked={actionsBlocked}
          />
        </WebContainer>

        {/* Voice recorder overlay */}
        <VoiceRecorder
          isRecording={isRecording}
          onStop={handleStopVoice}
          onCancel={handleCancelVoice}
          referenceText={activePronunciationDirective?.directive.reference_text}
        />

        {/* AI Consent Modal */}
        <AIConsentModal
          visible={showConsentModal}
          onAccept={handleConsentAccept}
          onDecline={handleConsentDecline}
        />

        {/* Quick Practice Sheet */}
        <QuickPracticeSheet
          visible={practiceVisible}
          loading={quickPractice.loading}
          error={quickPractice.error}
          content={quickPractice.data?.content ?? null}
          errorType={practiceParams?.errorType ?? ''}
          onClose={handlePracticeClose}
          onRetry={handlePracticeRetry}
        />

        {/* Report AI Content Dialog */}
        <ConfirmDialog
          visible={!!reportDialog}
          title={t('chat.report.title')}
          message={t('chat.report.question')}
          confirmLabel={t('chat.report.reportButton')}
          cancelLabel={t('chat.report.cancelButton')}
          destructive
          onConfirm={submitReport}
          onCancel={() => setReportDialog(null)}
        />
    </KAV>
    </View>
  );
}

const styles = StyleSheet.create({
  blurOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    opacity: 0.3,
  },
});
